import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { EVENT_TYPE_COLORS } from '../../constants/notificationTypes';
import { FaPlus, FaCalendarAlt, FaEye } from 'react-icons/fa';
import DayEventsModal from './DayEventsModal'; // ✅ AGGIUNTO: Import modal

const MonthView = ({
  currentDate,
  eventi,
  selectedDates = [],
  onDateSelect,
  onDateRangeSelect,
  onEventSelect,
  onMultiDayCreate,
  onTimeSlotClick,
  onDateSelectionAction // ✅ AGGIUNTO: Nuova prop per popup
}) => {
  const { theme } = useTheme();
  
  // Multi-selection state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [dragSelection, setDragSelection] = useState([]);
  const mouseDownRef = useRef(false);

  // ✅ AGGIUNTO: State per day events modal
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayForModal, setSelectedDayForModal] = useState(null);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of month and last day
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // ✅ MODIFICATO: Start from previous month's Monday (not Sunday)
    const startDate = new Date(firstDay);
    // getDay() returns 0=Sunday, 1=Monday, ... 6=Saturday
    // We want to start from Monday, so adjust accordingly
    const dayOfWeek = firstDay.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday (0), go back 6 days
    startDate.setDate(startDate.getDate() - daysFromMonday);
    
    // ✅ MODIFICATO: End at next month's Sunday (to complete the week)
    const endDate = new Date(lastDay);
    const lastDayOfWeek = lastDay.getDay();
    const daysToSunday = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
    endDate.setDate(endDate.getDate() + daysToSunday);
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, [currentDate]);

  // Group events by date
  const eventiPerGiorno = useMemo(() => {
    const map = {};
    eventi.forEach(evento => {
      const dateKey = new Date(evento.CAL_DataOraInizio).toDateString();
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(evento);
    });
    return map;
  }, [eventi]);

  // Helper functions
  const isSameDay = (date1, date2) => {
    return date1.toDateString() === date2.toDateString();
  };

  const isToday = (date) => isSameDay(date, new Date());
  
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const isSelected = (date) => {
    return selectedDates.some(selected => isSameDay(selected, date)) ||
           dragSelection.some(dragged => isSameDay(dragged, date));
  };

  const getDateRange = (start, end) => {
    const range = [];
    const current = new Date(Math.min(start.getTime(), end.getTime()));
    const final = new Date(Math.max(start.getTime(), end.getTime()));
    
    while (current <= final) {
      range.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return range;
  };

  // ✅ NUOVO: Trigger popup automatico quando la selezione cambia
  const triggerSelectionPopup = useCallback((dates, event) => {
    if (onDateSelectionAction && dates.length > 0) {
      console.log('📅 Auto-trigger popup per selezione:', dates);
      // Usa le coordinate del mouse o del centro dello schermo
      const rect = event?.target?.getBoundingClientRect() || { left: window.innerWidth / 2, top: window.innerHeight / 2 };
      onDateSelectionAction(dates, {
        target: event?.target || document.body,
        clientX: rect.left + (rect.width || 0) / 2,
        clientY: rect.top + (rect.height || 0) / 2,
        getBoundingClientRect: () => rect
      });
    }
  }, [onDateSelectionAction]);

  // Multi-select handlers
  const handleMouseDown = useCallback((date, event) => {
    event.preventDefault();
    mouseDownRef.current = true;
    
    // Check if clicking on an event
    if (event.target.closest('[data-event]')) {
      return; // Let event handler take over
    }
    
    if (event.ctrlKey || event.metaKey) {
      // Multi-select mode
      const newSelection = isSelected(date)
        ? selectedDates.filter(selected => !isSameDay(selected, date))
        : [...selectedDates, date];
      
      onDateSelect?.(newSelection);
      
      // ✅ NUOVO: Auto-trigger popup per multi-select
      if (newSelection.length > 0) {
        setTimeout(() => triggerSelectionPopup(newSelection, event), 100);
      }
    } else {
      // Start drag selection
      setIsSelecting(true);
      setSelectionStart(date);
      setDragSelection([date]);
    }
  }, [selectedDates, isSelected, onDateSelect, triggerSelectionPopup]);

  const handleMouseEnter = useCallback((date) => {
    if (isSelecting && selectionStart && mouseDownRef.current) {
      const range = getDateRange(selectionStart, date);
      setDragSelection(range);
    }
  }, [isSelecting, selectionStart]);

  const handleMouseUp = useCallback((event) => {
    mouseDownRef.current = false;
    
    if (isSelecting && dragSelection.length > 1) {
      // Complete drag selection
      onDateRangeSelect?.(dragSelection);
      
      // ✅ NUOVO: Auto-trigger popup per drag selection
      setTimeout(() => triggerSelectionPopup(dragSelection, event), 100);
      
      setDragSelection([]);
    }
    
    setIsSelecting(false);
    setSelectionStart(null);
  }, [isSelecting, dragSelection, onDateRangeSelect, triggerSelectionPopup]);

  // ✅ AGGIORNATO: Single click handler per day modal
  const handleCellClick = (date, event) => {
    // Se non è multi-select e non è drag, mostra day modal
    if (!event.ctrlKey && !event.metaKey && !isSelecting && isCurrentMonth(date)) {
      const dateKey = date.toDateString();
      const dayEvents = eventiPerGiorno[dateKey] || [];
      
      setSelectedDayForModal(date);
      setShowDayModal(true);
    }
  };

  // Event handlers
  const handleEventClick = (evento, event) => {
    event.stopPropagation();
    onEventSelect?.(evento);
  };

  // ✅ AGGIORNATO: Double click per nuovo evento
  const handleCellDoubleClick = (date) => {
    if (isCurrentMonth(date)) {
      setShowDayModal(false); // Chiudi day modal se aperto
      onTimeSlotClick?.(date, 9); // Default 9 AM
    }
  };

  // ✅ AGGIUNTO: Day modal handlers
  const handleDayModalClose = () => {
    setShowDayModal(false);
    setSelectedDayForModal(null);
  };

  const handleCreateNewFromDay = (date) => {
    setShowDayModal(false);
    onTimeSlotClick?.(date, 9);
  };

  // Render event dot
  const renderEventDot = (evento, maxVisible = 3, totalEvents = 0, dayEvents = []) => {
    const colorClass = EVENT_TYPE_COLORS[evento.CAL_Id_TipiEventi] || EVENT_TYPE_COLORS[evento.CAL_TipoEvento];
    const bgColor = colorClass?.bg || 'bg-gray-500';
    const textColor = colorClass?.text || 'text-white';
    
    const startTime = new Date(evento.CAL_DataOraInizio);
    const isAllDay = evento.CAL_GiornoIntero || 
      (startTime.getHours() === 0 && startTime.getMinutes() === 0);
    
    const eventTitle = evento.CAL_Titolo || 'Evento senza titolo';
    const eventTime = isAllDay ? 'Tutto il giorno' : 
      startTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    
    return (
      <div
        key={evento.CAL_Id_CalendarioEventi}
        className={`
          ${bgColor} ${textColor} text-xs px-2 py-1 rounded-md mb-1 
          cursor-pointer hover:opacity-90 hover:scale-[1.02] 
          transition-all duration-200 shadow-sm
          truncate max-w-full
        `}
        onClick={(e) => {
          e.stopPropagation();
          onEventSelect?.(evento);
        }}
        title={`${eventTitle} - ${eventTime}${evento.CAL_Descrizione ? `\n${evento.CAL_Descrizione}` : ''}`}
      >
        <div className="flex items-center gap-1">
          {!isAllDay && (
            <span className="text-xs opacity-75 font-mono">
              {startTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <span className="font-medium truncate flex-1">
            {eventTitle}
          </span>
        </div>
      </div>
    );
  };

  // ✅ IMPROVED: Indicatore per eventi nascosti più chiaro
  const renderMoreIndicator = (hiddenCount, allEvents, date) => {
    if (hiddenCount <= 0) return null;
    
    return (
      <div
        className={`
          text-xs ${mutedText} hover:${textColor} 
          cursor-pointer p-1 rounded 
          hover:bg-gray-100 dark:hover:bg-gray-700
          transition-colors text-center
        `}
        onClick={(e) => {
          e.stopPropagation();
          // Apri day modal con tutti gli eventi
          setSelectedDayForModal(date);
          setShowDayModal(true);
        }}
        title={`Visualizza tutti i ${hiddenCount + 3} eventi di questo giorno`}
      >
        <span className="font-medium">+{hiddenCount} altri</span>
        <div className="text-xs opacity-75">
          {hiddenCount === 1 ? 'evento' : 'eventi'}
        </div>
      </div>
    );
  };

  // Theme classes
  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const headerBg = theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const cellBorder = theme === 'dark' ? 'border-gray-600' : 'border-gray-200';

  // ✅ AGGIORNATO: Add global mouse up listener con event
  React.useEffect(() => {
    const handleGlobalMouseUp = (event) => handleMouseUp(event);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [handleMouseUp]);

  return (
    <>
      <div className={`${cardBg} rounded-lg border shadow-sm overflow-hidden select-none`}>
        {/* Header with weekdays */}
        <div className={`${headerBg} border-b ${cellBorder}`}>
          <div className="grid grid-cols-7 gap-0">
            {/* ✅ MODIFICATO: Inizia da Lunedì */}
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day, index) => (
              <div key={day} className={`p-3 text-center border-r last:border-r-0 ${cellBorder}`}>
                <span className={`text-sm font-medium ${mutedText} ${index === 5 || index === 6 ? 'text-red-500' : ''}`}>
                  {day}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0">
          {calendarDays.map((date, index) => {
            const dateKey = date.toDateString();
            const dayEvents = eventiPerGiorno[dateKey] || [];
            const isCurrentMonthDay = isCurrentMonth(date);
            const isTodayDay = isToday(date);
            const isSelectedDay = isSelected(date);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div
                key={dateKey}
                className={`
                  min-h-[120px] p-2 border-r border-b last-in-row:border-r-0 ${cellBorder}
                  cursor-pointer transition-all duration-200 relative group
                  ${!isCurrentMonthDay ? 'opacity-40' : ''}
                  ${isTodayDay ? 'bg-blue-50 dark:bg-blue-900/30' : ''}
                  ${isSelectedDay ? 'bg-emerald-100 dark:bg-emerald-900/50 ring-2 ring-emerald-500' : ''}
                  ${isWeekend && isCurrentMonthDay ? 'bg-red-50 dark:bg-red-900/20' : ''}
                  hover:bg-gray-100 dark:hover:bg-gray-700/50
                `}
                onMouseDown={(e) => handleMouseDown(date, e)}
                onMouseEnter={() => handleMouseEnter(date)}
                onClick={(e) => handleCellClick(date, e)} // ✅ AGGIUNTO: Single click
                onDoubleClick={() => handleCellDoubleClick(date)}
              >
                {/* Date number */}
                <div className="flex justify-between items-start mb-2">
                  <span className={`
                    text-sm font-medium
                    ${!isCurrentMonthDay ? mutedText : textColor}
                    ${isTodayDay ? 'bg-blue-600 text-white px-2 py-1 rounded-full text-xs' : ''}
                    ${isWeekend && isCurrentMonthDay ? 'text-red-600 dark:text-red-400' : ''}
                  `}>
                    {date.getDate()}
                  </span>

                  {/* Multi-day event creation hint */}
                  {isSelectedDay && (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400">
                      <FaCalendarAlt className="w-3 h-3" />
                    </div>
                  )}

                  {/* ✅ AGGIUNTO: Event count indicator */}
                  {dayEvents.length > 0 && isCurrentMonthDay && (
                    <div className={`
                      text-xs px-1.5 py-0.5 rounded-full bg-blue-600 text-white
                      flex items-center gap-1 opacity-75 hover:opacity-100 transition-opacity
                    `}>
                      <FaEye className="w-2 h-2" />
                      {dayEvents.length}
                    </div>
                  )}
                </div>

                {/* Events */}
                <div className="space-y-1">
                  {/* Event dots (first 3 events) */}
                  <div className="flex flex-wrap gap-1">
                    {dayEvents.slice(0, 3).map((evento, eventIndex) => 
                      renderEventDot(evento, eventIndex)
                    )}
                    {dayEvents.length > 3 && renderMoreIndicator(dayEvents.length - 3, dayEvents, date)}
                  </div>

                  {/* Event bars for all-day events */}
                  {dayEvents
                    .filter(evento => evento.CAL_GiornoIntero)
                    .slice(0, 2)
                    .map((evento, eventIndex) => {
                      const colorClass = EVENT_TYPE_COLORS[evento.CAL_TipoEvento] || 'bg-gray-500';
                      return (
                        <div
                          key={`bar-${evento.CAL_Id_CalendarioEventi}-${eventIndex}`}
                          data-event="true"
                          className={`
                            ${colorClass} text-white text-xs px-1 py-0.5 rounded truncate
                            cursor-pointer hover:opacity-80 transition-opacity
                          `}
                          title={evento.CAL_Titolo}
                          onClick={(e) => handleEventClick(evento, e)}
                        >
                          {evento.CAL_Titolo}
                        </div>
                      );
                    })
                  }
                </div>

                {/* Quick create on hover */}
                {isCurrentMonthDay && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className={`${theme === 'dark' ? 'bg-indigo-600' : 'bg-emerald-600'} text-white p-2 rounded-full pointer-events-auto shadow-lg`}>
                      <FaPlus className="w-3 h-3" />
                    </div>
                  </div>
                )}

                {/* Selection overlay */}
                {isSelecting && isSelectedDay && (
                  <div className="absolute inset-0 bg-emerald-500/20 border-2 border-emerald-500 rounded pointer-events-none" />
                )}
              </div>
            );
          })}
        </div>

        {/* ✅ RIMOSSO: Footer with multi-select actions (non più necessario) */}
        {selectedDates.length > 1 && (
          <div className={`${headerBg} border-t ${cellBorder} p-3`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${textColor}`}>
                {selectedDates.length} giorni selezionati
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => onMultiDayCreate?.(selectedDates)}
                  className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
                >
                  Crea Evento Multi-Giorno
                </button>
                <button
                  onClick={() => onDateSelect?.([])}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                >
                  Deseleziona
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Usage instructions */}
        <div className={`${headerBg} border-t ${cellBorder} p-2`}>
          <div className={`text-xs ${mutedText} text-center`}>
            <span className="font-medium">Click</span> per riepilogo eventi • 
            <span className="font-medium ml-1">Trascina</span> per selezionare range • 
            <span className="font-medium ml-1">Ctrl+Click</span> per selezione multipla • 
            <span className="font-medium ml-1">Doppio click</span> per nuovo evento
          </div>
        </div>
      </div>

      {/* ✅ AGGIUNTO: Day Events Modal */}
      <DayEventsModal
        isOpen={showDayModal}
        selectedDate={selectedDayForModal}
        events={selectedDayForModal ? eventiPerGiorno[selectedDayForModal.toDateString()] || [] : []}
        onClose={handleDayModalClose}
        onEventSelect={(evento) => {
          setShowDayModal(false);
          onEventSelect?.(evento);
        }}
        onCreateNew={handleCreateNewFromDay}
      />
    </>
  );
};

export default MonthView;